import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Button, Divider, CircularProgress } from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { getInventoryById } from "../../services/Inventory";
import BarcodeDisplay from "../common/BarcodeDisplay";

const ItemDetailsView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const itemId = id || searchParams.get('id');
  const [item, setItem] = useState<any>(location.state?.item || null);
  const [loading, setLoading] = useState(!location.state?.item);
  const formatDate = (date: any) => {
  if (!date) return "-";

  const d = new Date(date);

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
};

  useEffect(() => {
    const loadItem = async () => {
      if (location.state?.item) {
        setLoading(false);
        return;
      }
      if (!itemId) {
        setLoading(false);
        return;
      }
      try {
        const data = await getInventoryById(Number(itemId));
        setItem(data);
      } catch (error) {
        console.error("Failed to load item:", error);
      } finally {
        setLoading(false);
      }
    };
    loadItem();
  }, [itemId, location.state?.item]);

  if (loading) {
    return (
      <Box sx={{ p: 2, height: "calc(100vh - 3.5rem)", display: "flex", flexDirection: "column", gap: 1 }}>
        <Button
          onClick={() => navigate("/inventory/items")}
          sx={{ border: "none", textTransform: "none", color: "text.primary", alignSelf: "flex-start" }}
          startIcon={<ArrowBackIosNewOutlinedIcon fontSize="small" />}
        >
          Back
        </Button>

        <Paper sx={{ 
          p: 2, 
          borderRadius: "8px", 
          flex: 1, 
          display: "flex", 
          flexDirection: "column",
          justifyContent: "center", 
          alignItems: "center",
          gap: 2
        }}>
          <CircularProgress size="30px" />
          <Typography sx={{ color: "grey.600", fontSize: "14px" }}>Loading...</Typography>
        </Paper>
      </Box>
    );
  }

  const DetailItem = ({ label, value }: { label: string; value: any }) => (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.3 }}>
      <Typography sx={{ color: "grey.500", fontSize: "14px", fontWeight: 400 }}>{label}</Typography>
      <Typography sx={{ color: "text.primary", fontSize: "14px", fontWeight: 600 }}>{value || "-"}</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2, height: "calc(100vh - 3.5rem)", display: "flex", flexDirection: "column", gap: 1 }}>
      <Button
        onClick={() => navigate("/inventory/items")}
        sx={{ border: "none", textTransform: "none", color: "text.primary", alignSelf: "flex-start" }}
        startIcon={<ArrowBackIosNewOutlinedIcon fontSize="small" />}
      >
        Back
      </Button>

      <Paper sx={{ 
        p: 2, 
        borderRadius: "8px", 
        flex: 1, 
        overflow: "auto",
        "&::-webkit-scrollbar": {
          width: "8px"
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#CBD5E1",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#CBD5E1",
        },
        "&::-webkit-scrollbar-button": {
          display: "none",
        },
      }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Item Details</Typography>
          <Typography sx={{ color: "grey.600", fontSize: "0.875rem" }}>
            Detailed information for inventory item SKU #{item.id}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {item.imageUrl ? (
              <Box
                component="img"
                src={item.imageUrl}
                alt="Item Image"
                sx={{
                  width: "120px",
                  height: "120px",
                  objectFit: "cover",
                  borderRadius: "6px",
                  border: "1px solid",
                  borderColor: "grey.700",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "120px",
                  height: "120px",
                  bgcolor: "grey.100",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ color: "grey.500", fontSize: "0.75rem" }}>No image</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "stretch", minHeight: "60px" }}>
              <DetailItem label="Item Name" value={item.itemName} />
              <Divider orientation="vertical" flexItem  />
              <DetailItem label="Type" value={item.itemType} />
              <Divider orientation="vertical" flexItem />
              <DetailItem label="Category" value={item.category} />
              <Divider orientation="vertical" flexItem  />
              <DetailItem label="Item Supplier" value={item.supplierName} />
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "stretch", minHeight: "60px" }}>
              <DetailItem label="Item Model" value={item.model} />
              <Divider orientation="vertical" flexItem  />
              <DetailItem
                label="Dimensions (L X W X H cm)"
                value={item.lengthCm && item.widthCm && item.heightCm ? `${item.lengthCm} X ${item.widthCm} X ${item.heightCm}` : "-"}
              />
              <Divider orientation="vertical" flexItem />
              <DetailItem label="Purchase Date" value={formatDate(item.purchaseDate)} />
              <Divider orientation="vertical" flexItem  />
              <DetailItem label="Expiry Date" value={formatDate(item.expiryDate)} />
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "stretch", minHeight: "60px" }}>
              <DetailItem label="Purchase Price" value={item.itemCost ? `₹ ${item.itemCost}` : "-"} />
              <Divider orientation="vertical" flexItem />
              <DetailItem label="Selling Price" value={item.sellingPrice ? `₹ ${item.sellingPrice}` : "-"} />
              <Divider orientation="vertical" flexItem  />
              <DetailItem label="Current Location" value={item.location} />
              <Divider orientation="vertical" flexItem sx={{ visibility: "hidden" }} />
              <Box sx={{ flex: 1 }} /> {/* Empty space */}
            </Box>

            {/* Barcode Section  */}
            {item.barcode && (
              <Box sx={{ mt: 3, maxWidth: "400px" }}>
                <BarcodeDisplay 
                  value={item.barcode}
                  showPrint={true}
                  showLabel={true}
                  label="Barcode"
                  width={2}
                  height={60}
                  fontSize={12}
                  margin={10}
                  showPrintText={true}
                  labelStyle={{ color: "grey.500", fontSize: "14px", fontWeight: 400 }}
                  showRequired={false}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
export default ItemDetailsView;
